import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Loader2, AlertTriangle, Package, Tag } from "lucide-react";
import { alibabaApi } from "@/api/alibaba";

// ── Types ──

interface DataViewerRow {
  product_name?: string;
  company_name?: string;
  company_country?: string;
  price_min?: number;
  moq_text?: string;
  [key: string]: unknown;
}

interface AttrColumn {
  key: string;
  coverage: number;
  total: number;
  pct: number;
}

interface DataViewerSummary {
  totalProducts: number;
  totalCompanies: number;
  avgPrice: number;
  attrCount: number;
  levelA: number;
  levelAB: number;
  parsedOk: number;
  parsedPartial: number;
  parsedFailed: number;
  hasImages: number;
}

// ── Attribute group display names ──

const ATTR_GROUP_ICONS: Record<string, string> = {
  certification: "📋",
  material: "🧪",
  color: "🎨",
  size: "📏",
  other: "🔖",
};

function getAttrGroup(key: string): string {
  const k = key.toLowerCase();
  if (k.includes("certif") || k.includes("standard") || k.includes("approv")) return "certification";
  if (k.includes("material") || k.includes("fabric") || k.includes("composit")) return "material";
  if (k.includes("color") || k.includes("colour")) return "color";
  if (k.includes("size") || k.includes("dimension") || k.includes("length") || k.includes("width")) return "size";
  return "other";
}

// ── Section: Product List ──

function ProductList({
  rows,
  selectedCountry,
  onCountryChange,
}: {
  rows: DataViewerRow[];
  selectedCountry: string;
  onCountryChange: (c: string) => void;
}) {
  const countries = useMemo(() => {
    const set = new Set<string>();
    for (const row of rows) {
      const c = String(row.company_country ?? "");
      if (c) set.add(c);
    }
    return Array.from(set).sort();
  }, [rows]);

  const filtered = useMemo(() => {
    if (selectedCountry === "__all__") return rows;
    return rows.filter((r) => String(r.company_country ?? "") === selectedCountry);
  }, [rows, selectedCountry]);

  const COUNTRY_LABELS: Record<string, string> = {
    CN: "中國 CN",
    IN: "印度 IN",
    US: "美國 US",
    TW: "台灣 TW",
    KR: "韓國 KR",
    JP: "日本 JP",
    DE: "德國 DE",
    UK: "英國 UK",
  };

  return (
    <div className="space-y-3">
      {/* Country filter */}
      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-sm text-muted-foreground">篩選國家:</span>
        <Button
          size="sm"
          variant={selectedCountry === "__all__" ? "default" : "outline"}
          className="h-7 text-xs"
          onClick={() => onCountryChange("__all__")}
        >
          全部 ({rows.length})
        </Button>
        {countries.map((c) => {
          const count = rows.filter((r) => String(r.company_country ?? "") === c).length;
          return (
            <Button
              key={c}
              size="sm"
              variant={selectedCountry === c ? "default" : "outline"}
              className="h-7 text-xs"
              onClick={() => onCountryChange(c)}
            >
              {COUNTRY_LABELS[c] ?? c} ({count})
            </Button>
          );
        })}
      </div>

      {/* Table */}
      <div className="rounded-md border overflow-auto max-h-[500px]">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[40%]">產品名稱 Product</TableHead>
              <TableHead>廠商 Supplier</TableHead>
              <TableHead className="w-20">國家</TableHead>
              <TableHead className="w-28 text-right">最低價 USD</TableHead>
              <TableHead className="w-28">MOQ</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                  無資料
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((row, idx) => {
                const name = String(row.product_name ?? "");
                const company = String(row.company_name ?? "");
                const country = String(row.company_country ?? "");
                const priceMin = row.price_min != null ? Number(row.price_min) : null;
                const moq = String(row.moq_text ?? "");
                return (
                  <TableRow key={idx}>
                    <TableCell className="text-sm">
                      {name.length > 50 ? name.slice(0, 47) + "..." : name || "—"}
                    </TableCell>
                    <TableCell className="text-sm">{company || "—"}</TableCell>
                    <TableCell>
                      {country ? (
                        <Badge variant="outline" className="text-xs">{country}</Badge>
                      ) : "—"}
                    </TableCell>
                    <TableCell className="text-right text-sm font-mono">
                      {priceMin != null ? `$${priceMin.toFixed(2)}` : "—"}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {moq || "—"}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ── Section: Attribute Analysis ──

function AttributeAnalysis({ rows, attrColumns }: { rows: DataViewerRow[]; attrColumns: AttrColumn[] }) {
  // Group attributes by category
  const grouped = useMemo(() => {
    const groups: Record<string, Array<{ key: string; values: Record<string, number> }>> = {
      certification: [],
      material: [],
      color: [],
      size: [],
      other: [],
    };

    for (const col of attrColumns) {
      const group = getAttrGroup(col.key);
      // Count values across all rows
      const valueCounts: Record<string, number> = {};
      for (const row of rows) {
        const val = row[col.key];
        if (val != null && String(val).trim()) {
          const v = String(val).trim();
          valueCounts[v] = (valueCounts[v] ?? 0) + 1;
        }
      }
      if (Object.keys(valueCounts).length > 0) {
        groups[group].push({ key: col.key, values: valueCounts });
      }
    }
    return groups;
  }, [rows, attrColumns]);

  const hasAny = Object.values(grouped).some((g) => g.length > 0);
  if (!hasAny) {
    return (
      <p className="text-center text-muted-foreground py-8">尚無屬性分析資料</p>
    );
  }

  return (
    <div className="space-y-4">
      {(["certification", "material", "color", "size", "other"] as const).map((group) => {
        const items = grouped[group];
        if (items.length === 0) return null;
        const icon = ATTR_GROUP_ICONS[group];
        const label = {
          certification: "Certification 認證",
          material: "Material 材質",
          color: "Color 顏色",
          size: "Size 尺寸",
          other: "Other 其他屬性",
        }[group];
        return (
          <div key={group} className="space-y-2">
            <h4 className="text-sm font-semibold flex items-center gap-2">
              <span>{icon}</span>
              <span>{label}</span>
            </h4>
            {items.map(({ key, values }) => {
              const topValues = Object.entries(values)
                .sort(([, a], [, b]) => b - a)
                .slice(0, 15);
              return (
                <div key={key} className="ml-6">
                  <p className="text-xs text-muted-foreground mb-1 capitalize">
                    {key.replace(/_/g, " ")}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {topValues.map(([val, count]) => (
                      <Badge
                        key={val}
                        variant="secondary"
                        className="text-xs font-normal"
                      >
                        {val}
                        {count > 1 && (
                          <span className="ml-1 text-muted-foreground">×{count}</span>
                        )}
                      </Badge>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

// ── Main: AlibabaTab ──

interface AlibabaTabProps {
  keyword: string;
}

export default function AlibabaTab({ keyword }: AlibabaTabProps) {
  const [selectedCountry, setSelectedCountry] = useState("__all__");

  const { data, isLoading, error } = useQuery({
    queryKey: ["alibaba-data-viewer", keyword],
    queryFn: async () => {
      const res = await alibabaApi.dataViewer({ keyword, limit: 500 });
      return res.data;
    },
    enabled: !!keyword && keyword !== "Unknown",
  });

  const summary: DataViewerSummary | undefined = data?.summary;
  const rows: DataViewerRow[] = (data?.rows ?? []) as DataViewerRow[];
  const attrColumns: AttrColumn[] = data?.attrColumns ?? [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <span className="ml-3 text-muted-foreground">載入 Alibaba 資料…</span>
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <AlertTriangle className="h-10 w-10 mx-auto text-yellow-500 mb-3" />
          <p className="font-medium">無法載入 Alibaba 資料</p>
          <p className="text-sm text-muted-foreground mt-1">{(error as Error).message}</p>
        </CardContent>
      </Card>
    );
  }

  if (!data || rows.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Package className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <p className="font-medium">尚無 Alibaba 產品資料</p>
          <p className="text-sm text-muted-foreground mt-1">
            關鍵字「{keyword}」尚未爬取 Alibaba 資料
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary stats */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4 pb-3">
              <p className="text-xs text-muted-foreground">共計產品</p>
              <p className="text-2xl font-bold">{summary.totalProducts}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <p className="text-xs text-muted-foreground">廠商數</p>
              <p className="text-2xl font-bold">{summary.totalCompanies}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <p className="text-xs text-muted-foreground">平均價格 USD</p>
              <p className="text-2xl font-bold">
                {summary.avgPrice > 0 ? `$${summary.avgPrice.toFixed(2)}` : "—"}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <p className="text-xs text-muted-foreground">屬性欄位數</p>
              <p className="text-2xl font-bold">{attrColumns.length}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Section 1: Product list */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Package className="h-4 w-4" />
            產品列表 Product List
            <Badge variant="secondary" className="ml-auto">
              共 {rows.length} 件
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ProductList
            rows={rows}
            selectedCountry={selectedCountry}
            onCountryChange={setSelectedCountry}
          />
        </CardContent>
      </Card>

      {/* Section 2: Attribute analysis */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Tag className="h-4 w-4" />
            產品屬性分析 Attribute Analysis
          </CardTitle>
        </CardHeader>
        <CardContent>
          <AttributeAnalysis rows={rows} attrColumns={attrColumns} />
        </CardContent>
      </Card>
    </div>
  );
}
